import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { interval, Subscription } from 'rxjs';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { objectPropertiesToArray } from '@rallycommerce/common/utils.js';
import { OnboardingService, OnboardingStep } from '../../onboarding/onboarding.service';

@Component({
    selector: 'rosie-agent-setup-process',
    templateUrl: 'agent-setup-process.component.html',
    styleUrls: ['./agent-setup-process.component.scss'],
    standalone: false
})
export class AgentSetupProcessComponent implements OnInit, OnDestroy, AfterViewInit {
    @Input() progressSubject;
    @Input() errorMessage = '';
    @Input() shorterText = false;
    @Input() isShopify = false;
    @Input() defaultIntervalTime = 250;

    // Opt-in paced mode: give the first steps fixed, roughly equal durations and
    // let the last ("Generating") step wait for the real scrape. Two guarantees:
    //  - never cut short: every step is shown and completed before we finish;
    //  - never dawdle: the moment the scrape reports done (Finalize), we stop
    //    waiting and quickly play out whatever steps remain, then emit `completed`.
    // Off by default so the other consumers keep the original clock-driven feel.
    @Input() pacedSteps = false;
    @Input() stepDurations: number[] = [9000, 7000, 7000];
    @Output() completed = new EventEmitter<void>();

    faCheck = faCheck;
    private completedAgentSteps = [];
    private stepProgressSubscription: Subscription;
    private progressSubscription: Subscription;
    private pacedSubscription: Subscription;
    private finishSubscription: Subscription;
    private elapsedMs = 0;
    private scrapeDone = false;
    private finishing = false;

    // Escalating reassurance while the last step genuinely lingers (slow scrapes).
    // Thresholds are ms spent on the last step; the scrape is p99 ~76s total and
    // the last step begins ~23s in, so this covers ~50s of lingering (~73s total).
    private readonly reassuranceSchedule: { after: number; key: string }[] = [
        { after: 3000, key: 'AGENT.REASSURE_1' },
        { after: 18000, key: 'AGENT.REASSURE_2' },
        { after: 33000, key: 'AGENT.REASSURE_3' },
        { after: 48000, key: 'AGENT.REASSURE_4' },
    ];

    currentStep: AgentStep = AgentStep.Analyzing;
    inProgress = false;
    progressValue: number = 1;
    stepProgressValue: number = 1;
    hasProgressStarted = false;
    allStepsComplete = false;
    reassuranceKey = '';
    constructor(private onboarding: OnboardingService) {
    }

    ngOnInit(): void {
        this.progressSubject.subscribe((progressStatus: ProgressStatus) => {
            if (progressStatus === ProgressStatus.Start) {
                this.hasProgressStarted = true;
                this.startProgress();
            } else if (progressStatus === ProgressStatus.Stop) {
                this.stopProgress();
            } else if (progressStatus === ProgressStatus.Finalize) {
                this.handleFinalize();
            }
        });
    }

    ngAfterViewInit(): void {
        if (!this.hasProgressStarted) {
            this.startProgress();
        }
    }

    ngOnDestroy(): void {
        this.unsubscribeAll();
        this.progressValue = 1;
        this.stepProgressValue = 1;
        this.completedAgentSteps = [];
    }

    get agentSteps(): AgentStep[] {
        return objectPropertiesToArray(AgentStep);
    }

    get isLastStepLoading(): boolean {
        return this.currentStep === AgentStep.Generating;
    }

    isAgentStepComplete(step): boolean {
        return this.completedAgentSteps.includes(step);
    }

    getAgentStepTranslationKey(step): string {
        // For Shopify, the "processing" step reads "Learning your product catalog"
        // instead of "Processing your business information".
        const resolvedStep = (this.isShopify && step === AgentStep.Processing) ? 'PROCESSING_SHOPIFY' : step;
        return `AGENT.STEPS.${this.shorterText ? 'SHORTER_VERSION.' : ''}${resolvedStep}`;
    }

    private startProgress(): void {
        if (this.pacedSteps) {
            this.startPacedProgress();
        } else {
            this.startProgressIncrement();
        }
    }

    private handleFinalize(): void {
        if (this.pacedSteps) {
            // Don't snap. Flag the scrape as done; the paced tick picks this up and
            // accelerates through any remaining steps to finish quickly.
            this.scrapeDone = true;
        } else {
            this.finalizeProgress();
        }
    }

    // --- Paced mode -------------------------------------------------------------

    private startPacedProgress(): void {
        this.unsubscribeAll();
        this.elapsedMs = 0;
        this.scrapeDone = false;
        this.finishing = false;
        this.allStepsComplete = false;
        this.reassuranceKey = '';
        this.completedAgentSteps = [];
        this.currentStep = this.agentSteps[0];
        this.progressValue = 1;
        this.stepProgressValue = 0;

        const tick = 100;
        const boundaries = this.stepBoundaries();
        const lastStepStart = boundaries[boundaries.length - 1];
        const lastIndex = this.agentSteps.length - 1;

        this.pacedSubscription = interval(tick).subscribe(() => {
            this.elapsedMs += tick;
            if (this.allStepsComplete) {
                return;
            }

            // Scrape finished — stop pacing and rush the rest to completion.
            if (this.scrapeDone && !this.finishing) {
                this.accelerateFinish();
                return;
            }

            let idx = boundaries.findIndex((boundary) => this.elapsedMs < boundary);
            if (idx === -1) {
                idx = lastIndex;
            }

            for (let i = 0; i < idx; i++) {
                if (!this.completedAgentSteps.includes(this.agentSteps[i])) {
                    this.completedAgentSteps.push(this.agentSteps[i]);
                }
            }
            this.currentStep = this.agentSteps[idx];
            this.stepProgressValue = this.pacedStepRing(idx, boundaries, lastStepStart);
            this.progressValue = this.pacedBar(lastStepStart);
            this.reassuranceKey = idx === lastIndex ? this.reassuranceFor(this.elapsedMs - lastStepStart) : '';
        });
    }

    // Quickly play out any remaining steps (each still briefly shown + checked)
    // and finish. Used both when the scrape beats the animation and to cap the
    // last step once it's done waiting.
    private accelerateFinish(): void {
        if (this.finishing) {
            return;
        }
        this.finishing = true;
        this.reassuranceKey = '';
        if (this.pacedSubscription) {
            this.pacedSubscription.unsubscribe();
        }

        const lastIndex = this.agentSteps.length - 1;
        const ringStep = 10; // ~500ms to fill a step at the 50ms cadence below

        this.finishSubscription = interval(50).subscribe(() => {
            this.stepProgressValue = Math.min(100, this.stepProgressValue + ringStep);
            const idx = this.agentSteps.indexOf(this.currentStep);
            this.progressValue = Math.max(
                this.progressValue,
                Math.min(100, ((idx + this.stepProgressValue / 100) / this.agentSteps.length) * 100),
            );

            if (this.stepProgressValue < 100) {
                return;
            }
            if (!this.completedAgentSteps.includes(this.currentStep)) {
                this.completedAgentSteps.push(this.currentStep);
            }
            if (idx >= lastIndex) {
                this.finishSubscription?.unsubscribe();
                this.completedAgentSteps = this.agentSteps;
                this.allStepsComplete = true;
                this.progressValue = 100;
                this.completed.emit();
            } else {
                this.currentStep = this.agentSteps[idx + 1];
                this.stepProgressValue = 0;
            }
        });
    }

    private reassuranceFor(lingeringMs: number): string {
        let key = '';
        for (const entry of this.reassuranceSchedule) {
            if (lingeringMs >= entry.after) {
                key = entry.key;
            }
        }
        return key;
    }

    private stepBoundaries(): number[] {
        const boundaries: number[] = [];
        let sum = 0;
        for (const duration of this.stepDurations) {
            sum += duration;
            boundaries.push(sum);
        }
        return boundaries;
    }

    private pacedStepRing(idx: number, boundaries: number[], lastStepStart: number): number {
        if (idx >= this.stepDurations.length) {
            // Last step is open-ended: ease toward (but never reach) full while it
            // waits for the scrape, so it visibly progresses without looking done.
            const waited = this.elapsedMs - lastStepStart;
            return Math.min(90, (1 - Math.exp(-waited / 12000)) * 100);
        }
        const prevBoundary = idx === 0 ? 0 : boundaries[idx - 1];
        const withinStep = (this.elapsedMs - prevBoundary) / this.stepDurations[idx];
        return Math.max(0, Math.min(100, withinStep * 100));
    }

    private pacedBar(lastStepStart: number): number {
        if (this.elapsedMs < lastStepStart) {
            return Math.min(75, 1 + (this.elapsedMs / lastStepStart) * 74);
        }
        const waited = this.elapsedMs - lastStepStart;
        return Math.min(99, 75 + (1 - Math.exp(-waited / 15000)) * 17);
    }

    // --- Legacy clock-driven mode (unchanged) -----------------------------------

    private startProgressIncrement(): void {
        const stopAtNumber = 100;
        let incrementBy = 1;
        let intervalTime = 0;
        if (this.onboarding.chosenPath === OnboardingStep.GoogleLookupVerification) {
            intervalTime = 25;
        } else if (this.onboarding.chosenPath === OnboardingStep.BusinessInfo) {
            intervalTime = 20;
        } else {
            intervalTime = this.defaultIntervalTime;
        }
        this.progressSubscription = interval(intervalTime).subscribe(() => {
            if (this.progressValue < stopAtNumber) {
                this.progressValue += (this.progressValue > 95 ? 0.05 : incrementBy);
                if (this.progressValue % 25 === 0 || this.progressValue > 95) {
                    if (!this.isLastStepLoading) {
                        this.completedAgentSteps.push(this.currentStep);
                        this.currentStep = this.agentSteps[Math.floor(this.progressValue / 25)];
                        this.stepProgressValue = 0;
                    }
                }
            }
        });

        this.stepProgressSubscription = interval(intervalTime / 4).subscribe(() => {
            if (this.stepProgressValue < 100 && this.progressValue < stopAtNumber) {
                this.stepProgressValue += incrementBy;
            }
        });
    }

    private finalizeProgress(): void {
        this.unsubscribeAll();
        this.completedAgentSteps = this.agentSteps;
        this.allStepsComplete = true;
        this.stepProgressValue = 100;
        this.progressValue = 100;
    }

    private stopProgress() {
        this.unsubscribeAll();
        this.completedAgentSteps = [];
        this.progressValue = 0;
        this.stepProgressValue = 0;
        this.reassuranceKey = '';
        this.allStepsComplete = false;
        this.finishing = false;
    }

    private unsubscribeAll(): void {
        this.progressSubscription?.unsubscribe();
        this.stepProgressSubscription?.unsubscribe();
        this.pacedSubscription?.unsubscribe();
        this.finishSubscription?.unsubscribe();
    }
}

export enum AgentStep {
    Analyzing = 'ANALYZING',
    Processing = 'PROCESSING',
    Optimizing = 'OPTIMIZING',
    Generating = 'GENERATING',
}

export enum ProgressStatus {
    Start = 'START',
    Stop = 'STOP',
    Finalize = 'FINALIZE',
}
